import React, { useCallback, useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import { CircularProgress, Skeleton, useTheme } from "@mui/material";
import { TranscriptListItem } from "../../../shared/interfaces/Transcript";
import { TranscriptCard } from "./TranscriptCard";
import { EmptyTranscriptsState } from "./EmptyTranscriptsState";

interface TranscriptGridProps {
  isLoading: boolean;
  transcripts: TranscriptListItem[];
  hasMore: boolean;
  onLoadMore: () => void;
  hasActiveFilters: boolean;
}

const SkeletonCard: React.FC = () => {
  const theme = useTheme();
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
      <Skeleton
        variant="rectangular"
        sx={{
          width: "100%",
          height: 280,
          borderRadius: "12px",
          bgcolor: theme.palette.action.hover,
        }}
      />
    </Grid>
  );
};

export const TranscriptGrid: React.FC<TranscriptGridProps> = ({
  isLoading,
  transcripts,
  hasMore,
  onLoadMore,
  hasActiveFilters,
}) => {
  const [hasLoaded, setHasLoaded] = useState(!isLoading);

  // The observer reads the latest loadMore state through this ref, so the
  // observer instance never has to be rebuilt when these props change.
  const observeState = useRef({ isLoading, hasMore, onLoadMore });
  observeState.current = { isLoading, hasMore, onLoadMore };
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Callback ref: (re)attaches the IntersectionObserver to the sentinel whenever
  // it mounts. This survives the empty-state early return unmounting and later
  // remounting the footer — a deps-based effect can miss that remount and leave
  // the observer detached, silently stalling infinite scroll.
  const setSentinel = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const { isLoading, hasMore, onLoadMore } = observeState.current;
        if (entries[0].isIntersecting && !isLoading && hasMore) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => {
    if (!isLoading && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [isLoading, hasLoaded]);

  const isInitialLoading = isLoading && !hasLoaded;
  const isEmpty = !isLoading && transcripts.length === 0;

  if (isEmpty) {
    return (
      <Grid size={{ xs: 12 }}>
        <EmptyTranscriptsState hasActiveFilters={hasActiveFilters} />
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12 }}>
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignContent="flex-start"
        sx={{
          minHeight: { xs: "auto", sm: "550px" },
          maxWidth: "100%",
        }}
      >
        {isInitialLoading
          ? Array(8)
              .fill(0)
              .map((_, i) => <SkeletonCard key={`skeleton-${i}`} />)
          : transcripts.map((t) => (
              <TranscriptCard
                key={`${t.evidenceId}-${t.versionNumber}`}
                transcript={t}
              />
            ))}

        <Grid size={{ xs: 12 }}>
          <div
            ref={setSentinel}
            style={{ width: "100%", height: 20, marginTop: 10 }}
          />
        </Grid>

        {!isInitialLoading && isLoading && (
          <Grid
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center", p: 2 }}
          >
            <CircularProgress />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
